import React, { useState } from "react";
import "./VesselNominationAgreementModal.css";

const VesselNominationAgreementModal = ({ 
  isOpen, 
  onClose, 
  onSign, 
  isLoading,
  charterRequest,
  profileData 
}) => {
  const [showSigningModal, setShowSigningModal] = useState(false);
  const [signatureData, setSignatureData] = useState({
    representativeName: "",
    signatureDate: new Date().toISOString().split('T')[0]
  });

  if (!isOpen) return null;

  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  const handleReviewAndSign = () => {
    setShowSigningModal(true);
  };

  const handleAgreeAndSign = () => {
    if (!signatureData.representativeName.trim()) {
      // Use a visual indicator - could add toast here if needed
      return;
    }
    onSign();
  };

  return (
    <div className="vessel-nomination-modal-overlay" onClick={onClose}>
      <div className="vessel-nomination-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="vessel-nomination-modal-header">
          <h2>VESSEL NOMINATION AGREEMENT</h2>
        </div>
        
        <div className="vessel-nomination-modal-body">
          <div className="agreement-intro">
            <p>
              THIS VESSEL NOMINATION AGREEMENT is made this {currentDate} between
            </p>
          </div>

          <div className="parties-section">
            <div className="party-column">
              <p>
                <strong>VATUG</strong> of Nigeria (hereinafter described as "VATUG" which expression shall, where the context so admits, include its successors-in-title and assigns), AND
              </p>
            </div>
            <div className="party-column">
              <p>
                <strong>{profileData?.companyName || "Charterer"}</strong>, of {profileData?.address || "Address"} {profileData?.city || ""} {profileData?.state || ""} {profileData?.country || ""}. (hereinafter described as "the Charterer" which expression shall, where the context so admits, include its successors-in-title and assignments).
              </p>
            </div>
          </div>

          <div className="whereas-section">
            <h3>WHEREAS</h3>
            <ol>
              <li>VATUG is designed to increase efficiency of finding and engaging offshore support vessels, tankers and ships whereby vessel owners can publish vessels available for hire, and charterers can request to hire available vessels.</li>
              <li>VATUG has received a request from an organization seeking to hire a vessel ("Charterer").</li>
              <li>VATUG intends to nominate a vessel for hire by the Charterer.</li>
              <li>The Parties herein have agreed to enter into this Agreement which shall outline the indicative terms and conditions of their relationship in relation to the eventual hire of the Owner's vessel.</li>
              <li>The Vessel Nomination Details in Part A constitute part of this Agreement, and shall be read in conjunction with the terms in Part B.</li>
            </ol>
          </div>

          <div className="agreement-intro">
            <p>The Parties hereby agree as follows:</p>
          </div>

          {/* Part A: Vessel Nomination Details */}
          <div className="part-a-section">
            <h3>PART A: VESSEL NOMINATION DETAILS</h3>
            <div className="nomination-details-table">
              <div className="nomination-row">
                <div className="nomination-label">Name of Vessel</div>
                <div className="nomination-value">{charterRequest?.vessel || "N/A"}</div>
              </div>
              <div className="nomination-row">
                <div className="nomination-label">Charterer/End User</div>
                <div className="nomination-value">{profileData?.companyName || "N/A"}</div>
              </div>
              <div className="nomination-row">
                <div className="nomination-label">Scope of Work</div>
                <div className="nomination-value">{charterRequest?.scopeOfWork || "N/A"}</div>
              </div>
              <div className="nomination-row">
                <div className="nomination-label">Duration</div>
                <div className="nomination-value">{charterRequest?.duration || "N/A"} days</div>
              </div>
              <div className="nomination-row">
                <div className="nomination-label">Location of Operation</div>
                <div className="nomination-value">
                  {charterRequest?.origin?.name || charterRequest?.locations?.[0]?.name || "N/A"}
                </div>
              </div>
              <div className="nomination-row">
                <div className="nomination-label">Mobilization Date</div>
                <div className="nomination-value">
                  {charterRequest?.startDate ? new Date(charterRequest.startDate).toLocaleDateString() : "N/A"}
                </div>
              </div>
              <div className="nomination-row">
                <div className="nomination-label">Proposed Vessel Day Rate</div>
                <div className="nomination-value">
                  {charterRequest?.finalProposedPrice ? `$${charterRequest.finalProposedPrice.toLocaleString()}` : "N/A"}
                </div>
              </div>
              <div className="nomination-row">
                <div className="nomination-label">Commission (Rate)</div>
                <div className="nomination-value">As per platform terms</div>
              </div>
            </div>
          </div>

          {/* Part B: Standard Terms and Conditions */}
          <div className="part-b-section">
            <h3>PART B: STANDARD TERMS AND CONDITIONS</h3>

            <div className="terms-section">
              <h4>Relationships</h4>
              <ol>
                <li>Upon execution of this Agreement, VATUG shall formally introduce the Owner to the Charterer to discuss the terms of the proposed Vessel hire.</li>
                <li>VATUG shall act solely as an intermediary between the Owner and the Charterer.</li>
                <li>Should the Owner agree on terms of hire with the Charterer in respect of the Vessel or any other vessel within the Owner's ownership and/or control, the Owner enters into a direct contractual relationship with the Charterer.</li>
                <li>Nothing in this Agreement shall be construed as creating a partnership, agency, employment or franchise relationship between the Owner and VATUG, and neither shall have the right, power or authority to create any obligation or duty, express or implied, on behalf of the other Party, it being understood that the Parties are independent contractors vis-a-vis one another.</li>
              </ol>
            </div>

            <div className="terms-section">
              <h4>Commission</h4>
              <ol>
                <li>In the event that an agreement to hire the Vessel is concluded between the Owner and the Charterer, the Charterer shall pay a Commission to VATUG at the rate stated in Part A, on the total consideration accepted as the Charter Fee by the Owner in respect of the Vessel.</li>
                <li>Commission shall be paid within 48 hours of receipt of payment by the Owner. Where the Charterer makes payment in instalments, VATUG shall be paid the appropriate Commission on each instalment.</li>
              </ol>
            </div>

            <div className="terms-section">
              <h4>Non-circumvention & Non-solicitation</h4>
              <ol>
                <li>The Owner agrees that it shall not solicit, canvass, contact, deal with or otherwise become involved with the Charterer in respect of the hire of the Vessel without the involvement of VATUG. The Owner acknowledges that in the event of violation of this covenant, the Owner shall be liable to VATUG in damages.</li>
              </ol>
            </div>

            <div className="terms-section">
              <h4>General</h4>
              <ol>
                <li>The Owner shall not assign its rights or responsibilities under this Agreement without the prior written consent of VATUG and any unauthorized assignment shall be null and void. Notwithstanding such consent, the Owner shall remain responsible for the performance of the applicable terms of this Agreement by VATUG, unless otherwise agreed at the time of such assignment.</li>
                <li>No waiver of any provision of this Agreement shall be effective unless made in writing and signed by the waiving party. The failure of any party to require the performance of any term or obligation of this Agreement, or the waiver by any party of any breach of this Agreement, shall not prevent any subsequent enforcement of such term or obligation or be deemed a waiver of any subsequent breach.</li>
                <li>In the event that any provision of this Agreement is deemed void, invalid or unenforceable, that provision shall be severed from the remainder of the Agreement. All remaining provisions of this Agreement shall then continue in full force.</li>
                <li>This Agreement together with the VATUG Terms of Use and Privacy Policy constitute the entire Agreement between the parties. Any amendment of this Vessel Nomination Agreement must be in writing.</li>
              </ol>
            </div>

            <div className="terms-section">
              <h4>Governing Law</h4>
              <ol>
                <li>This Agreement shall be governed by, and construed in accordance with, the laws of the Federal Republic of Nigeria.</li>
              </ol>
            </div>

            <div className="terms-section">
              <h4>Dispute Resolution</h4>
              <ol>
                <li>If any dispute arises out of or in connection with this Agreement, the Parties shall resolve the dispute amicably within 30 calendar days. If for any reason the Parties are unable to resolve the dispute, the Parties agree to enter into mediation in good faith to settle such a dispute at the Rivers State Multi-Door Courthouse.</li>
                <li>No Party may commence any court proceedings arising out of this Agreement until it has attempted to settle the dispute by mediation and either the mediation has terminated or the other Party has failed to participate in the mediation.</li>
              </ol>
            </div>

            <div className="witness-clause">
              <p>In witness of the above, the Parties execute this Agreement on the date first above written</p>
            </div>
          </div>
        </div>

        <div className="vessel-nomination-modal-footer">
          <button 
            className="btn-back" 
            onClick={onClose}
            disabled={isLoading}
          >
            Back
          </button>
          <button 
            className="btn-sign" 
            onClick={handleReviewAndSign}
            disabled={isLoading}
          >
            Review and Sign
          </button>
        </div>
      </div>

      {/* Signing Modal */}
      {showSigningModal && (
        <div className="signing-modal-overlay" onClick={() => setShowSigningModal(false)}>
          <div className="signing-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="signing-modal-header">
              <h3>Signing Agreement</h3>
              <button onClick={() => setShowSigningModal(false)}>&times;</button>
            </div>
            
            <div className="signing-modal-body">
              <div className="signing-parties">
                {/* VATUG Signature Section */}
                <div className="signing-party">
                  <div className="party-icon">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                  </div>
                  <p className="on-behalf-of">on behalf of</p>
                  <p className="company-name">VATUG</p>
                  <div className="signature-field">
                    <label>Representative Name</label>
                    <input 
                      type="text" 
                      placeholder="Enter name"
                      disabled
                      value="VATUG Representative"
                    />
                  </div>
                  <div className="signature-field">
                    <label>Date</label>
                    <input 
                      type="date" 
                      disabled
                      value={signatureData.signatureDate}
                    />
                  </div>
                </div>

                {/* Charterer/Vessel Owner Signature Section */}
                <div className="signing-party">
                  <div className="party-icon">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/>
                      <line x1="8" y1="2" x2="8" y2="6"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                  </div>
                  <p className="on-behalf-of">on behalf of</p>
                  <p className="company-name">{profileData?.companyName || "Company Name"}</p>
                  <div className="signature-field">
                    <label>Representative Name</label>
                    <input 
                      type="text" 
                      placeholder="Enter name"
                      value={signatureData.representativeName}
                      onChange={(e) => setSignatureData(prev => ({ ...prev, representativeName: e.target.value }))}
                    />
                  </div>
                  <div className="signature-field">
                    <label>Date</label>
                    <input 
                      type="date" 
                      value={signatureData.signatureDate}
                      onChange={(e) => setSignatureData(prev => ({ ...prev, signatureDate: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="signing-modal-footer">
              <button 
                className="btn-agree-sign" 
                onClick={handleAgreeAndSign}
                disabled={isLoading || !signatureData.representativeName.trim()}
              >
                {isLoading ? "Signing..." : "Agree and Sign"}
              </button>
              <button 
                className="btn-cancel-sign" 
                onClick={() => setShowSigningModal(false)}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VesselNominationAgreementModal;

